import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, ThumbsDown, ThumbsUp, Wand2 } from "lucide-react";

import { aiApi, type SuggestTeabatesResponse } from "lib/api/ai";
import { getAiUiStrings, resolveAiLocale } from "i18n/aiUi";

interface AiSuggestionPanelProps {
  issueId: number;
  excludedTeabateIds?: number[];
  onUseSuggestion: (claim: string) => void;
}

type FeedbackState = -1 | 1 | null;

/**
 * Cap on the number of previously-shown claims we forward back to the
 * server so the cache key and prompt stay bounded while still giving the
 * model enough context to avoid paraphrasing prior suggestions.
 */
const MAX_PREVIOUS_CLAIMS_SENT = 20;

/**
 * Prefix used for per-suggestion feedback keys so each card gets its own
 * thumbs-up/thumbs-down state. Index is reset per response.
 */
function getSuggestionFeedbackKey(index: number): string {
  return `suggestion-${index}`;
}

function getContestednessLabel(value: SuggestTeabatesResponse["suggestions"][number]["contestednessBand"]): string {
  const ui = getAiUiStrings(resolveAiLocale());
  switch (value) {
    case "broad_agreement":
      return ui.broadAgreementLabel;
    case "highly_contested":
      return ui.highlyContestedLabel;
    case "contested_but_legible":
    default:
      return ui.contestedButLegibleLabel;
  }
}

const AiSuggestionPanel: React.FC<AiSuggestionPanelProps> = ({
  issueId,
  excludedTeabateIds = [],
  onUseSuggestion,
}) => {
  const ui = useMemo(() => getAiUiStrings(resolveAiLocale()), []);
  const [isLoading, setIsLoading] = useState(false);
  const [submittingFeedbackKey, setSubmittingFeedbackKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feedbackStateByKey, setFeedbackStateByKey] = useState<Record<string, FeedbackState>>({});
  const [response, setResponse] = useState<SuggestTeabatesResponse | null>(null);
  const [seenClaims, setSeenClaims] = useState<string[]>([]);
  const [hasRegeneratedOnce, setHasRegeneratedOnce] = useState(false);
  const [lastResponseWasEmpty, setLastResponseWasEmpty] = useState(false);

  useEffect(() => {
    setError(null);
    setFeedbackStateByKey({});
    setResponse(null);
    setSeenClaims([]);
    setHasRegeneratedOnce(false);
    setLastResponseWasEmpty(false);
  }, [issueId]);

  const loadSuggestions = useCallback(async (excludePreviousClaims: string[]) => {
    setIsLoading(true);
    setError(null);
    setLastResponseWasEmpty(false);

    try {
      const nextResponse = await aiApi.suggestTeabates({
        excludeExistingTeabateIds: excludedTeabateIds,
        excludePreviousClaims: excludePreviousClaims.slice(-MAX_PREVIOUS_CLAIMS_SENT),
        issueId,
        maxSuggestions: 3,
      });
      const nextClaims = nextResponse.suggestions.map((suggestion) => suggestion.claim);
      setResponse(nextResponse);
      setFeedbackStateByKey({});
      setLastResponseWasEmpty(nextClaims.length === 0);
      setSeenClaims((current) => {
        const merged = [...current];
        for (const claim of nextClaims) {
          if (!merged.includes(claim)) {
            merged.push(claim);
          }
        }
        return merged;
      });
    } catch (caughtError: unknown) {
      const fallbackMessage = ui.searchSuggestionsError;
      const maybeMessage = typeof caughtError === "object" && caughtError && "response" in caughtError
        ? String(((caughtError as { response?: { data?: { message?: string } } }).response?.data?.message) || fallbackMessage)
        : fallbackMessage;
      setError(maybeMessage);
    } finally {
      setIsLoading(false);
    }
  }, [excludedTeabateIds, issueId, ui.searchSuggestionsError]);

  const handleInitialLoad = useCallback(() => {
    void loadSuggestions([]);
  }, [loadSuggestions]);

  const handleRegenerate = useCallback(() => {
    setHasRegeneratedOnce(true);
    void loadSuggestions(seenClaims);
  }, [loadSuggestions, seenClaims]);

  const handleFeedback = useCallback(async (suggestionKey: string, rating: -1 | 1) => {
    if (!response?.meta.invocationId || submittingFeedbackKey) {
      return;
    }

    const nextRating = feedbackStateByKey[suggestionKey] === rating ? 0 : rating;
    setSubmittingFeedbackKey(suggestionKey);
    try {
      await aiApi.submitFeedback({
        invocationId: response.meta.invocationId,
        rating: nextRating,
        suggestionKey,
      });
      setFeedbackStateByKey((current) => ({
        ...current,
        [suggestionKey]: nextRating === 0 ? null : nextRating,
      }));
    } finally {
      setSubmittingFeedbackKey(null);
    }
  }, [feedbackStateByKey, response?.meta.invocationId, submittingFeedbackKey]);

  const suggestions = response?.suggestions ?? [];
  const hasResults = suggestions.length > 0;
  const regenerateDisabled = isLoading || (lastResponseWasEmpty && hasRegeneratedOnce);

  return (
    <section className="rounded-2xl border border-eoe-border-main bg-eoe-bg-hover p-4 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
            {ui.suggestClaimsTitle}
          </p>
          <p className="mt-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {ui.suggestClaimsSubtitle}
          </p>
          {response?.meta.awardCost ? (
            <p className="mt-2 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {ui.thisCostsAwards(response.meta.awardCost)}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          {hasResults || lastResponseWasEmpty ? (
            <button
              type="button"
              onClick={() => handleRegenerate()}
              disabled={regenerateDisabled}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
                regenerateDisabled ? "btn-primary-disabled opacity-60 cursor-not-allowed" : "btn-secondary"
              }`}
            >
              {isLoading ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="size-4" aria-hidden />
              )}
              {isLoading ? ui.regeneratingClaimsLoading : ui.regenerateClaimsAction}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleInitialLoad()}
              disabled={isLoading}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
                isLoading ? "btn-primary-disabled opacity-60 cursor-not-allowed" : "btn-secondary"
              }`}
            >
              {isLoading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Wand2 className="size-4" aria-hidden />}
              {isLoading ? ui.findingClaimsLoading : ui.suggestClaimsAction}
            </button>
          )}
        </div>
      </div>

      {error ? (
        <p className="mt-3 text-sm text-warning-red dark:text-dark-warning-red">
          {error}
        </p>
      ) : null}

      {hasResults ? (
        <div className="mt-4 space-y-3">
          {suggestions.map((suggestion, index) => {
            const suggestionKey = getSuggestionFeedbackKey(index);
            const feedbackState = feedbackStateByKey[suggestionKey] ?? null;
            const isSubmittingFeedback = submittingFeedbackKey === suggestionKey;

            return (
            <article
              key={suggestion.claim}
              className="rounded-2xl border border-eoe-border-main bg-white/70 p-4 dark:border-eoe-dark-border-main dark:bg-black/10"
            >
              <div className="flex flex-col items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="mb-2 inline-flex rounded-full bg-eoe-blue/10 px-2 py-1 text-xs font-semibold text-eoe-blue dark:bg-eoe-blue/20">
                    {getContestednessLabel(suggestion.contestednessBand)}
                  </div>
                  <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                    {suggestion.claim}
                  </p>
                  <p className="mt-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {suggestion.reasoning}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onUseSuggestion(suggestion.claim)}
                  className="btn-primary px-4 py-2 text-sm"
                >
                  {ui.useClaimAction}
                </button>
              </div>

              {suggestion.sources.length ? (
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {ui.sourceLabel}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {suggestion.sources.map((source) => (
                      <a
                        key={source.url}
                        href={source.url}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full border border-eoe-border-main px-3 py-1 text-xs text-eoe-text-sub hover:text-eoe-text dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
                      >
                        {source.title || source.publisher || source.url}
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleFeedback(suggestionKey, 1)}
                  aria-label={ui.feedbackPositive}
                  disabled={isSubmittingFeedback}
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${
                    feedbackState === 1
                      ? "bg-green-600 text-white"
                      : "border border-eoe-border-main text-eoe-text-sub dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub"
                  }`}
                >
                  <ThumbsUp className="size-3.5" aria-hidden />
                  {ui.feedbackPositive}
                </button>
                <button
                  type="button"
                  onClick={() => void handleFeedback(suggestionKey, -1)}
                  aria-label={ui.feedbackNegative}
                  disabled={isSubmittingFeedback}
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${
                    feedbackState === -1
                      ? "bg-red-600 text-white"
                      : "border border-eoe-border-main text-eoe-text-sub dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub"
                  }`}
                >
                  <ThumbsDown className="size-3.5" aria-hidden />
                  {ui.feedbackNegative}
                </button>
                {feedbackState ? (
                  <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {ui.feedbackThanks}
                  </span>
                ) : null}
              </div>
            </article>
            );
          })}

          <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {ui.aiDisclaimer}
          </p>
        </div>
      ) : null}

      {!hasResults && lastResponseWasEmpty ? (
        <p className="mt-3 text-sm italic text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {ui.noMoreSuggestions}
        </p>
      ) : null}
    </section>
  );
};

export default AiSuggestionPanel;
